import apolloClient from 'api/apolloClient';
import {
  GET_CUSTOMER_CAMPAIGNS,
  GET_CUSTOMER_CAMPAIGN_BY_ID,
  GET_CUSTOMER_CAMPAIGNS_COUNT,
  GET_CAMPAIGN_USERS
} from './queries/campaigns';
import { parseCampaignFromGraphQL } from 'services/campaignGraphQLServices';
import {
  GET_PAST_DUE_INVOICES,
  GET_PAYOFF_INVOICES,
  GET_BILL_PAY_INVOICE_BY_NUMBER,
} from './queries/campaignDetails';

/**
 * Fetch campaigns for the logged-in customer using GraphQL
 * @param {Object} params - Query parameters
 * @param {number} params.first - Number of campaigns to fetch
 * @param {string} params.after - Cursor for pagination
 * @returns {Promise<Object>} Campaigns data with pagination info
 */
export const fetchGetCampaignsGraphQL = async ({ first = 25, after = null } = {}) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CUSTOMER_CAMPAIGNS,
      variables: {
        first,
        after,
      },
      fetchPolicy: "network-only", // Always fetch fresh data from network
    });

    const { customerCampaignsForCustomer } = data;

    // Parse campaigns to match the existing format
    const campaigns = customerCampaignsForCustomer.nodes.map((campaign) => parseCampaignFromGraphQL(campaign));

    return {
      campaigns,
      totalCount: customerCampaignsForCustomer.totalCount,
      pageInfo: customerCampaignsForCustomer.pageInfo,
      // For backward compatibility with existing pagination logic
      totalPages: Math.ceil(customerCampaignsForCustomer.totalCount / first)
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch a single campaign by ID using GraphQL
 * @param {Object} params
 * @param {number} params.id - Campaign ID
 * @returns {Promise<Object>} Campaign data
 */
export const fetchGetCampaignGraphQL = async ({ id }) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CUSTOMER_CAMPAIGN_BY_ID,
      variables: {
        campaignId: parseInt(id, 10),
      },
      fetchPolicy: "network-only", // Always fetch fresh data from network
    });

    const { customerCampaignsForCustomer } = data;

    if (!customerCampaignsForCustomer.nodes || customerCampaignsForCustomer.nodes.length === 0) {
      throw new Error('Campaign not found');
    }

    const campaign = parseCampaignFromGraphQL(customerCampaignsForCustomer.nodes[0]);

    return campaign;
  } catch (error) {
    throw error;
  }
};

/**
 * Get total count of customer campaigns
 * @returns {Promise<number>} Total count
 */
export const fetchGetCampaignsCountGraphQL = async () => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CUSTOMER_CAMPAIGNS_COUNT,
      fetchPolicy: 'network-only' // Always fetch fresh data from network
    });

    return data.customerCampaignsForCustomer.totalCount;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch campaign users/team members using GraphQL
 * @param {Object} params
 * @param {number} params.campaignId - Campaign ID
 * @returns {Promise<Array>} Campaign users array
 */
export const fetchGetCampaignUsersGraphQL = async ({ campaignId }) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_CAMPAIGN_USERS,
      variables: {
        campaignId: parseInt(campaignId, 10),
      },
      fetchPolicy: 'network-only'
    });

    return data.campaignUsers;
  } catch (error) {
    throw error;
  }
};


export const fetchGetPastDueInvoicesByCampaignGraphQL = async ({ campaignId }) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_PAST_DUE_INVOICES,
      variables: {
        campaignId: parseInt(campaignId, 10),
      },
      fetchPolicy: 'network-only'
    });

    return data.pastDueInvoicesByCampaign;
  } catch (error) {
    console.error('Error while fetching  payments:', error);
    throw error;
  }
};


export const fetchPayOffInvoicesByCampaignGraphQL = async ({ campaignId }) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_PAYOFF_INVOICES,
      variables: {
        campaignId: parseInt(campaignId, 10),
      },
      fetchPolicy: 'network-only'
    });

    return data.pendingInvoices;
  } catch (error) {
    console.error('Error while fetching all pay off payments:', error);
    throw error;
  }
};

/**
 * Fetch a single invoice for the public bill-pay page (email link with invoiceId).
 * Returns an array so callers can share the same list mapping as payoff/past-due flows.
 */
export const fetchBillPayInvoiceByNumberGraphQL = async ({ campaignId, invoiceNumber }) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_BILL_PAY_INVOICE_BY_NUMBER,
      variables: {
        campaignId: parseInt(campaignId, 10),
        invoiceNumber: String(invoiceNumber),
      },
      fetchPolicy: 'network-only',
    });

    const invoice = data.billPayInvoiceByNumber;
    return invoice ? [invoice] : [];
  } catch (error) {
    console.error('Error while fetching bill pay invoice by number:', error);
    throw error;
  }
};